import { type FC, useEffect, useState } from "react";
import moment from "moment";
import { Carousel } from "../../../Common/Carousel/Carousel";
import { CancellablePromise } from "../../../Utilities/Generic/Promises";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { getTempOption, setTempOption } from "../../../Utilities/Platform/Options/Options";
import { LoginNewsItem, type LoginNewsItemType } from "./LoginNewsItem";

interface LoginNewsPost extends LoginNewsItemType {
    id: number;
}

interface ServerBlogResponse {
    blogs: Array<{
        author: {
            name: string;
            image: string;
        };
        categoryName: string;
        createdAt: string;
        excerpt: string;
        id: number;
        image: string;
        sectionId: string;
        title: string;
        updatedAt: string;
    }>;
}

export const LoginNews: FC = () => {
    const [ loading, setLoading ] = useState(false);
    const [ news, setNews ] = useState<Array<LoginNewsPost>>([]);

    useEffect(() => {
        if(process.env.NODE_ENV === "test") {
            return;
        }

        const cached = getTempOption<Array<LoginNewsPost>>("shopfront:news", []);

        if(cached.length) {
            setNews(cached);

            return;
        }

        setLoading(true);
        setNews([]);

        const url = new URL(process.env.SHOPFRONT_NEWS_URL as string);

        const newsPromise = new CancellablePromise(
            fetch(url)
                .then(response => response.json())
                .then(async (response: ServerBlogResponse) => {
                    const lastMonth = moment().subtract(1, "month");

                    const formatted = [];

                    for(let i = 0, l = response.blogs.length; i < l; i++) {
                        const date = moment(response.blogs[i].createdAt);

                        if(date.isBefore(lastMonth)) {
                            continue;
                        }

                        let image = response.blogs[i].image;

                        if(!image) {
                            image = "https://cdn.onshopfront.com/imgs/logo.png?cache=true";
                        } else if(image.startsWith("/")) {
                            image = new URL(image, url).toString();
                        }

                        formatted.push({
                            id  : response.blogs[i].id,
                            url : `${process.env.SHOPFRONT_BLOG_URL as string}/${response.blogs[i].id}`,
                            text: response.blogs[i].title,
                            image,
                            date,
                        });
                    }

                    return formatted;
                })
        );

        newsPromise
            .then(formatted => {
                setTempOption("shopfront:news", formatted);
                setNews(formatted);
            })
            .catch(err => {
                if(err.cancelled) {
                    return;
                }

                console.error("Error while parsing or fetching the news", err);
            })
            .finally(() => {
                setLoading(false);
            });

        return () => {
            newsPromise.cancel();
        };
    }, []);

    if(loading) {
        return (
            <div className="loading" key="loading">
                <FontAwesomeLight icon="spinner-third" spin />
            </div>
        );
    }

    if(news.length === 0) {
        return (
            <div className="no-news" key="no-news">
                <div><FontAwesomeLight icon="newspaper" /></div>
                <p>We have no news to show you at this time.</p>
            </div>
        );
    }

    return (
        <Carousel changeTime={10000}>
            {news.map((item) => (
                <LoginNewsItem key={item.id} {...item} />
            ))}
        </Carousel>
    );
};
